import { useCallback, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { PagePopover } from '@traceio-org/ui';

export interface MenuOption<T extends string | number = string> {
  value: T;
  label: string;
  /** Shown but not choosable (a status the server would refuse from here). */
  disabled?: boolean;
}
/** Keys' name for the same shape. */
export type MenuSelectOption = MenuOption<string>;

/**
 * A choice among a few options in Trace UI's own menu, never a native select: the date-range control of its
 * operational stats (OperationalStats, the "Last 30 days" filter of Orders, Quoting and the Brokers home), rebuilt
 * from the same parts because Trace UI exports it only inside that stats strip. The same PagePopover (variant
 * operations-period) and menuitemradio markup and classes, so Trace UI's styles and Admin's dark fixes apply; the same
 * keyboard: the menu opens on the checked choice, arrows move (and wrap), Home and End jump, Enter or a click chooses
 * and closes, Escape closes with focus back on the trigger.
 *
 * Two triggers:
 * - field (default): a form-field-like button under a label (shown, or for assistive technology only with
 *   showLabel false), named by the label and the current choice (Keys' reminder and project filter);
 * - compact: Trace UI's period trigger, outlined, named "<label>: <choice>" (Health's periods and app filters).
 *
 * The icons are drawn inline from the same shapes (lucide's chevron and check): lucide is Trace UI's dependency, not
 * Admin's.
 */
export function MenuSelect<T extends string | number>({
  label,
  value,
  options,
  onChange,
  showLabel = true,
  icon,
  align,
  variant = 'field',
  open: openProp,
  onOpenChange,
  labelInTrigger = false,
}: {
  /** What the choice is for: the field's label, the menu's name ("Remind me", "Charts period"). */
  label: string;
  value: T;
  options: readonly MenuOption<T>[];
  onChange: (value: T) => void;
  /** Field variant: false keeps the label for assistive technology only (a toolbar filter). */
  showLabel?: boolean;
  /** Leads the trigger (the period menus' calendar); none by default. */
  icon?: ReactNode;
  /** Where the menu hangs from the trigger: start for fields, end for compact triggers by default. */
  align?: 'start' | 'center' | 'end';
  variant?: 'field' | 'compact';
  /**
   * Controlled opening, for a page that opens the menu itself (a ticket's S, P and A shortcuts). Without it the menu
   * keeps its own state, as before.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Compact variant: show "<label>: <choice>" on the trigger (a filter toolbar), not the choice alone. */
  labelInTrigger?: boolean;
}) {
  const [ownOpen, setOwnOpen] = useState(false);
  const open = openProp ?? ownOpen;
  const setOpen = (next: boolean) => {
    if (openProp === undefined) setOwnOpen(next);
    onOpenChange?.(next);
  };
  const labelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement | null>(null);
  const current = options.find((option) => option.value === value) ?? options[0]!;
  const focusMenu = useCallback((element: HTMLDivElement | null) => {
    menu.current = element;
    if (!element) return;
    // The portal and its focus manager mount together; focus the checked choice afterwards (as Trace UI does).
    requestAnimationFrame(() => {
      if (menu.current !== element || !element.isConnected) return;
      element.querySelector<HTMLElement>('[aria-checked="true"]')?.focus({ preventScroll: true });
    });
  }, []);
  const choose = (next: T) => {
    onChange(next);
    setOpen(false);
  };
  const move = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const next =
      event.key === 'ArrowDown'
        ? (index + 1) % options.length
        : event.key === 'ArrowUp'
          ? (index - 1 + options.length) % options.length
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? options.length - 1
              : -1;
    if (next < 0) return;
    event.preventDefault();
    const buttons = menu.current?.querySelectorAll<HTMLElement>('[role="menuitemradio"]');
    buttons?.[next]?.focus({ preventScroll: true });
    buttons?.[next]?.scrollIntoView?.({ block: 'nearest' });
  };
  const chevron = (
    <svg
      aria-hidden="true"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
  const trigger =
    variant === 'field' ? (
      <button
        ref={triggerRef}
        type="button"
        className="admin-menu-select__trigger"
        aria-labelledby={`${labelId} ${labelId}-value`}
        aria-haspopup="menu"
      >
        {icon}
        <span id={`${labelId}-value`}>{current.label}</span>
        {chevron}
      </button>
    ) : (
      <button
        ref={triggerRef}
        type="button"
        className="trace-operational-stats-period-trigger admin-period-trigger"
        aria-label={`${label}: ${current.label}`}
        aria-haspopup="menu"
      >
        {icon}
        {labelInTrigger ? (
          <span>
            <span className="admin-period-trigger__label">{label}:</span> {current.label}
          </span>
        ) : (
          <span>{current.label}</span>
        )}
        {chevron}
      </button>
    );
  const popover = (
    <PagePopover
      label={label}
      variant="operations-period"
      align={align ?? (variant === 'field' ? 'start' : 'end')}
      open={open}
      onOpenChange={setOpen}
      returnFocus={triggerRef}
      trigger={trigger}
    >
      <div ref={focusMenu} className="trace-operational-stats-period-menu" role="menu" aria-label={label}>
        {options.map((option, index) => (
          <button
            key={option.value}
            type="button"
            className="trace-operational-stats-period-option"
            role="menuitemradio"
            aria-checked={option.value === current.value}
            tabIndex={option.value === current.value ? 0 : -1}
            disabled={option.disabled}
            onClick={() => choose(option.value)}
            onKeyDown={(event) => move(event, index)}
          >
            <span>{option.label}</span>
            {option.value === current.value ? (
              <svg
                aria-hidden="true"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            ) : null}
          </button>
        ))}
      </div>
    </PagePopover>
  );
  if (variant === 'compact') return popover;
  return (
    <div className="admin-menu-select">
      <span id={labelId} className={showLabel ? 'admin-field__label' : 'admin-sr-only'}>
        {label}
      </span>
      {popover}
    </div>
  );
}

/** The period menus (Health's charts and uptime grid): the compact MenuSelect with Trace UI's calendar. */
export function PeriodMenu<T extends string | number>(props: {
  label: string;
  value: T;
  options: readonly MenuOption<T>[];
  onChange: (value: T) => void;
}) {
  return (
    <MenuSelect
      {...props}
      variant="compact"
      icon={
        <svg
          className="trace-operational-stats-calendar"
          aria-hidden="true"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5.252 2.75v-1M10.748 2.75v-1M3.026 6.25h9.99" />
          <rect width="11.479" height="11" x="2.26" y="2.75" rx="3" />
        </svg>
      }
    />
  );
}
